import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Inbox } from "lucide-react";
import { containersApi } from "../lib/api";
import { formatDate, freshnessLabel } from "../lib/format";

/**
 * Modal listing active containers that are likely to spoil soon —
 * matches the backend "expiring_soon" stat (days_until_expiry <= 2, includes
 * anything past best-by). When the list is empty, we show one of a handful of
 * lighthearted "your fridge is fine" messages, picked at random on each open.
 */
const CHEEKY_EMPTY_LINES = [
  "No food's going rotten on your watch.",
  "Fridge status: thriving.",
  "All fresh, no drama.",
  "Nothing's plotting mold in there.",
  "Your fridge is having a really good day.",
  "Zero science experiments detected.",
  "Everything's chillin' — literally.",
];

const pickLine = () =>
  CHEEKY_EMPTY_LINES[Math.floor(Math.random() * CHEEKY_EMPTY_LINES.length)];

export const SpoilSoonOverlay = ({ open, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Freeze one cheeky line for the duration of a single open — rotates on
  // next open. Rebuilt via useMemo whenever `open` flips true.
  const emptyLine = useMemo(() => (open ? pickLine() : ""), [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await containersApi.list({
          status: "fresh",
          sort: "expiring_soon",
        });
        if (cancelled) return;
        // "Likely to spoil soon" = active & within 2 days of best-by (or past).
        setItems(list.filter((c) => (c.days_until_expiry ?? 999) <= 2));
      } catch (err) {
        console.warn("Failed to load spoil-soon list:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-md flex items-end sm:items-center justify-center"
          data-testid="spoil-soon-overlay-backdrop"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-6 sm:m-4 max-h-[88vh] overflow-y-auto"
            data-testid="spoil-soon-overlay"
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#FBEAE5] flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#B04A2A]" strokeWidth={2.6} />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#B04A2A]">
                    Heads up
                  </div>
                  <div className="font-heading text-xl font-bold text-[#2D3142]">
                    Likely to spoil soon
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="spoil-soon-overlay-close"
                aria-label="Close"
                className="w-9 h-9 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loading ? (
              <div className="mt-6 tup-card p-6 text-center text-sm text-[#8A93A6]">
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div
                className="mt-8 tup-card p-8 text-center"
                data-testid="spoil-soon-empty"
              >
                <Inbox className="w-8 h-8 mx-auto text-[#4A8A78]" strokeWidth={2} />
                <div
                  className="font-heading text-lg font-semibold text-[#2D3142] mt-4 leading-snug"
                  data-testid="spoil-soon-empty-line"
                >
                  {emptyLine}
                </div>
                <p className="text-xs text-[#8A93A6] mt-3 leading-relaxed">
                  Nothing in your fridge is within 2 days of its best-by date.
                </p>
              </div>
            ) : (
              <>
                <p className="text-xs text-[#4F5D75] mt-3 leading-relaxed">
                  These need eyes on them — tap any container to update, mark it eaten,
                  or discard.
                </p>
                <ul
                  className="mt-4 space-y-2.5"
                  data-testid="spoil-soon-list"
                >
                  {items.map((c) => {
                    const past = (c.days_until_expiry ?? 0) < 0;
                    return (
                      <li key={c.id}>
                        <Link
                          to={`/container/${c.id}`}
                          onClick={onClose}
                          data-testid={`spoil-soon-item-${c.id}`}
                          className={`block tup-card p-3.5 hover:bg-[#F5F3ED] active:scale-[0.99] transition-all border-l-4 ${
                            past ? "border-[#D95D39]" : "border-[#E07A5F]"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="font-heading text-base font-semibold text-[#2D3142] truncate">
                                {c.name}
                              </div>
                              <div className="text-[11px] text-[#4F5D75] mt-0.5">
                                Best-by {formatDate(c.expiration_date)}
                                {c.box_number ? ` · Box ${c.box_number}` : ""}
                              </div>
                            </div>
                            <span
                              className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${
                                past
                                  ? "bg-[#FBE2DA] text-[#8C2A0F]"
                                  : "bg-[#FBEAE5] text-[#B04A2A]"
                              }`}
                            >
                              {freshnessLabel(c)}
                            </span>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
